/**
 * Tessellates a 2D XZ polygon into a flat liquid-surface mesh at a given
 * world-space Y. Also computes a per-vertex distance to the polygon boundary
 * for the shader's shoreline fade.
 *
 * Used by both the asset-manager editor (live preview) and the runtime
 * SceneManifestLoader so the preview and the final render use the same mesh.
 *
 * The polygon is ear-clipped, then every triangle is uniformly subdivided
 * (4-way midpoint split with a shared edge cache so adjacent triangles agree
 * on midpoint vertex IDs — no T-junctions). Subdivision depth is chosen so
 * the longest original-triangle edge is ≤ `maxEdgeLength`. Without this,
 * a sparse polygon (e.g. a 4-vertex square) gives the wave shader nothing
 * to displace and the surface stays flat.
 */

export type PolygonVertex = readonly [x: number, z: number];

export type LiquidPolygonGeometry = {
  positions: Float32Array;
  texCoords: Float32Array;
  indices: Uint16Array | Uint32Array;
  edgeDistance: Float32Array;
};

export type TessellateOptions = {
  /** World-space texture scale; UVs = (x / scale, z / scale). */
  textureScale?: number;
  /**
   * Target maximum edge length (metres) after subdivision. Lower = denser
   * mesh = finer ripples. Default 0.4 m comfortably Nyquist-samples the
   * highest wave octave (~0.5 m wavelength in the liquid-surface vertex
   * shader). Pass Infinity to skip subdivision entirely.
   */
  maxEdgeLength?: number;
  /** Hard cap on subdivision depth. Each level multiplies triangle count by 4. */
  maxSubdivisionDepth?: number;
};

/**
 * Tessellate a simple (non-self-intersecting) polygon at `fillY`.
 * Polygon vertices are [x, z] pairs, given in any winding — the routine
 * detects orientation and normalises internally.
 */
export function tessellateLiquidPolygon(
  polygon: readonly PolygonVertex[],
  fillY: number,
  options: TessellateOptions = {},
): LiquidPolygonGeometry {
  if (polygon.length < 3) {
    throw new Error(`liquid polygon needs ≥3 vertices, got ${polygon.length}`);
  }
  const textureScale = options.textureScale ?? 4;
  const maxEdgeLength = options.maxEdgeLength ?? 0.4;
  const maxDepth = options.maxSubdivisionDepth ?? 6;

  // Work on a mutable copy with guaranteed CCW winding (in X-Z space, where
  // +Y is up, CCW when viewed from +Y ⇒ positive signed area).
  const loop: PolygonVertex[] = polygon.map(([x, z]) => [x, z]);
  if (signedArea(loop) < 0) loop.reverse();

  const baseTris = earClip(loop);

  // Mutable vertex pool — starts with the polygon corners and grows during
  // subdivision. Each entry is [x, z]. Y is added later when we emit the
  // final Float32Array.
  const verts: number[][] = loop.map(([x, z]) => [x, z]);

  const depth = chooseDepth(verts, baseTris, maxEdgeLength, maxDepth);
  const finalTris = depth > 0 ? subdivide(verts, baseTris, depth) : baseTris;

  // Compute the original boundary edge list once for distance queries.
  const boundaryEdges: number[] = [];
  for (let i = 0; i < loop.length; i++) {
    boundaryEdges.push(i, (i + 1) % loop.length);
  }

  const vertexCount = verts.length;
  const positions = new Float32Array(vertexCount * 3);
  const texCoords = new Float32Array(vertexCount * 2);
  const edgeDistance = new Float32Array(vertexCount);

  for (let i = 0; i < vertexCount; i++) {
    const [x, z] = verts[i];
    positions[i * 3 + 0] = x;
    positions[i * 3 + 1] = fillY;
    positions[i * 3 + 2] = z;
    texCoords[i * 2 + 0] = x / textureScale;
    texCoords[i * 2 + 1] = z / textureScale;
    edgeDistance[i] = distanceToBoundary(x, z, loop, boundaryEdges);
  }

  // Reverse winding before emitting. The earClip + subdivide pipeline reasons in
  // 2D XZ with the "CCW from +Y" convention, but those triangles project to 3D
  // with a -Y geometric normal — meaning gl_FrontFacing is false when viewed
  // from above, and the fragment shader's two-sided flip mirrors the wave
  // normal downward. Flipping (b,c) makes the 3D triangle normal point +Y.
  for (let t = 0; t < finalTris.length; t += 3) {
    const tmp = finalTris[t + 1];
    finalTris[t + 1] = finalTris[t + 2];
    finalTris[t + 2] = tmp;
  }

  const indexArray =
    vertexCount <= 0xffff ? new Uint16Array(finalTris) : new Uint32Array(finalTris);

  return { positions, texCoords, indices: indexArray, edgeDistance };
}

// ── Signed area (CCW positive in XZ with +Y up) ─────────────────────────────
function signedArea(loop: readonly PolygonVertex[]): number {
  let a = 0;
  for (let i = 0; i < loop.length; i++) {
    const [x1, z1] = loop[i];
    const [x2, z2] = loop[(i + 1) % loop.length];
    a += x1 * z2 - x2 * z1;
  }
  return a * 0.5;
}

// ── Ear clipping ────────────────────────────────────────────────────────────
/**
 * O(n²) ear-clipping triangulator for a simple CCW polygon. Returns indices
 * into the input loop. Polygons in practice are ≤ a few hundred verts, so the
 * quadratic cost is fine and the lack of an external dep is worth more than
 * shaving a log-n factor.
 */
function earClip(loop: readonly PolygonVertex[]): number[] {
  const n = loop.length;
  if (n < 3) return [];
  if (n === 3) return [0, 1, 2];

  const prev = new Int32Array(n);
  const next = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    prev[i] = (i - 1 + n) % n;
    next[i] = (i + 1) % n;
  }

  const tris: number[] = [];
  let remaining = n;
  let guard = 0;
  const maxGuard = 2 * n * n; // classical fallback

  let i = 0;
  while (remaining > 3) {
    if (++guard > maxGuard) {
      // Degenerate polygon — emit what we have and stop rather than hang.
      break;
    }
    const a = prev[i];
    const b = i;
    const c = next[i];
    if (isEar(loop, a, b, c, next)) {
      tris.push(a, b, c);
      next[a] = c;
      prev[c] = a;
      remaining--;
      i = a; // step back so we can re-check the chain
    } else {
      i = next[i];
    }
  }
  tris.push(prev[i], i, next[i]);
  return tris;
}

function isEar(
  loop: readonly PolygonVertex[],
  a: number,
  b: number,
  c: number,
  next: Int32Array,
): boolean {
  const [ax, az] = loop[a];
  const [bx, bz] = loop[b];
  const [cx, cz] = loop[c];
  // Convex test (CCW): cross of (b-a)×(c-b) must be positive.
  if ((bx - ax) * (cz - az) - (bz - az) * (cx - ax) <= 0) return false;

  // No other vertex may lie inside triangle abc.
  let k = next[c];
  while (k !== a) {
    if (k !== a && k !== b && k !== c) {
      const [px, pz] = loop[k];
      if (pointInTriangle(px, pz, ax, az, bx, bz, cx, cz)) return false;
    }
    k = next[k];
  }
  return true;
}

function pointInTriangle(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  cx: number,
  cz: number,
): boolean {
  const d1 = (px - bx) * (az - bz) - (ax - bx) * (pz - bz);
  const d2 = (px - cx) * (bz - cz) - (bx - cx) * (pz - cz);
  const d3 = (px - ax) * (cz - az) - (cx - ax) * (pz - az);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

// ── Uniform subdivision (4-way midpoint, shared edge cache) ─────────────────
/**
 * Choose subdivision depth K so the longest base-triangle edge, after K
 * uniform splits, falls below `maxEdgeLength`. Capped at `maxDepth` to bound
 * vertex count for very large polygons.
 */
function chooseDepth(
  verts: readonly number[][],
  tris: readonly number[],
  maxEdgeLength: number,
  maxDepth: number,
): number {
  if (!isFinite(maxEdgeLength) || maxEdgeLength <= 0) return 0;
  let maxLen = 0;
  for (let t = 0; t < tris.length; t += 3) {
    const a = tris[t], b = tris[t + 1], c = tris[t + 2];
    maxLen = Math.max(maxLen, edgeLen(verts, a, b), edgeLen(verts, b, c), edgeLen(verts, c, a));
  }
  if (maxLen <= maxEdgeLength) return 0;
  const k = Math.ceil(Math.log2(maxLen / maxEdgeLength));
  return Math.min(maxDepth, Math.max(0, k));
}

function edgeLen(verts: readonly number[][], a: number, b: number): number {
  const dx = verts[a][0] - verts[b][0];
  const dz = verts[a][1] - verts[b][1];
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Subdivide every triangle 4-way recursively `depth` times. A `Map` keyed by
 * the unordered pair of endpoint indices ensures adjacent triangles share
 * midpoint vertex IDs, which keeps the mesh watertight (no T-junctions →
 * no wave-displacement cracks).
 */
function subdivide(verts: number[][], tris: readonly number[], depth: number): number[] {
  let current: number[] = tris.slice();
  for (let d = 0; d < depth; d++) {
    const midCache = new Map<number, number>();
    const next: number[] = [];
    const getMid = (a: number, b: number): number => {
      const lo = a < b ? a : b;
      const hi = a < b ? b : a;
      // 26-bit shift comfortably fits practical vertex counts (< 67M).
      const key = lo * 0x4000000 + hi;
      const cached = midCache.get(key);
      if (cached !== undefined) return cached;
      const idx = verts.length;
      verts.push([(verts[a][0] + verts[b][0]) * 0.5, (verts[a][1] + verts[b][1]) * 0.5]);
      midCache.set(key, idx);
      return idx;
    };
    for (let t = 0; t < current.length; t += 3) {
      const a = current[t], b = current[t + 1], c = current[t + 2];
      const ab = getMid(a, b);
      const bc = getMid(b, c);
      const ca = getMid(c, a);
      next.push(a, ab, ca, ab, b, bc, ca, bc, c, ab, bc, ca);
    }
    current = next;
  }
  return current;
}

// ── Per-vertex distance to polygon boundary ─────────────────────────────────
/**
 * Distance from any (x, z) to the nearest original boundary edge. Used to
 * drive the shader's `uShoreFadeDistance` geometric fade — close-to-shore
 * vertices get attenuated wave displacement.
 *
 * Boundary edges are the input polygon's edges only; subdivision-introduced
 * edges don't bound the surface and would corrupt the shore fade.
 */
function distanceToBoundary(
  px: number,
  pz: number,
  loop: readonly PolygonVertex[],
  edges: readonly number[],
): number {
  let best = Infinity;
  for (let e = 0; e < edges.length; e += 2) {
    const [ax, az] = loop[edges[e]];
    const [bx, bz] = loop[edges[e + 1]];
    const d = pointSegmentDistance(px, pz, ax, az, bx, bz);
    if (d < best) best = d;
  }
  return best === Infinity ? 0 : best;
}

function pointSegmentDistance(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq === 0) {
    const ex = px - ax;
    const ez = pz - az;
    return Math.sqrt(ex * ex + ez * ez);
  }
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cz = az + t * dz;
  const ex = px - cx;
  const ez = pz - cz;
  return Math.sqrt(ex * ex + ez * ez);
}
